import React from 'react';
import styled, { keyframes } from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import PropTypes from 'prop-types';
import {
  GatsbyImage,
  getImage,
} from 'gatsby-plugin-image';

// diptychs are triggered by a gallery of two images in the arc file. The description
// of the gallery serves as the single caption for both images. If photo/copy dictates
// that the caption containes left/top/right/bottom identifiers within the caption,
// span tags may need to be added around these within the caption, and then display
// properties set for each depending on screen size

const floatUpAnimation = keyframes`
  0% {transform: translateY(0)}
  25% {transform: translateY(-8px)}
  75% {transform: translateY(3px)}
  100% {transform; translateY(0)}
`;

const floatDownAnimation = keyframes`
  0% {transform: translateY(0)}
  25% {transform: translateY(8px)}
  75% {transform: translateY(-3px)}
  100% {transform; translateY(0)}
`;

const DiptychWrapper = styled.div`
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 20px;
  @media (max-width: 900px) {
    max-width: 700px;
  }
`;

const ImagesContainer = styled.div`
  > div:first-of-type {
    float: left;
  }
  > div:last-of-type {
    float: right;
    margin-top: 75px;
  }
  &:after {
    content: '';
    display: table;
    clear: both;
  }
  @media (max-width: 900px) {
    > div:first-of-type {
      float: none;
      margin-bottom: 1rem;
    }
    > div:last-of-type {
      float: none;
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  &:after {
    content: '';
    display: table;
    clear: both;
  }
`;

const ImageWrapper = styled.div`
  width: 48%;
  animation-name: ${floatDownAnimation};
  animation-duration: 15s;
  animation-iteration-count: infinite;
  &:last-of-type {
    animation-name: ${floatUpAnimation}
  }
  > div {
    background-color: var(--custom-white);
    padding: 10px;
    border-radius: 3px;
    box-shadow: var(--customShadow);
  }
  @media (max-width: 900px) {
    max-width: 700px;
    width: 100%;
  }
`;

const DiptychCaption = styled.p`
  margin: 1rem auto 0;
  font-size: 0.75rem;
  line-height: 1rem;
  width: 100%;
  max-width: 660px;
`;

const Diptych = ({ description, images }) => {
  const reOrderedImages = images.sort((a, b) => (
    a.additional_properties.galleryOrder - b.additional_properties.galleryOrder
  ));
  return (
    <DiptychWrapper>
      <ImagesContainer>
        {reOrderedImages.map((image, index) => (
          <ImageWrapper key={`dip-${image._id}`}>
            <div>
              <GatsbyImage className="diptych__image" alt={image.caption} image={getImage(image.localImage)} key={image._id} />
            </div>
          </ImageWrapper>
        ))}
      </ImagesContainer>
      <DiptychCaption className="benton-n4 caption__mobile">{ReactHtmlParser(description.basic)}</DiptychCaption>
    </DiptychWrapper>
  );
};

export default Diptych;

Diptych.propTypes = {
  description: PropTypes.shape({
    basic: PropTypes.string,
  }).isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      localImage: PropTypes.shape({}),
      caption: PropTypes.string,
      _id: PropTypes.string,
    }),
  ).isRequired,
};
